import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, Clock, RefreshCw, CheckCircle, XCircle, AlertCircle, Calendar as CalendarIcon,
  Search, BarChart2, Users, Plus, List, Layers, Eye, Edit2, Trash2, ChevronDown, RotateCcw
} from 'lucide-react';
import CreateTaskModal from './CreateTaskModal';
import BulkAssignTaskModal from './BulkAssignTaskModal';
import PerformanceReportModal from './PerformanceReportModal';
import EmployeeTasksModal from './EmployeeTasksModal';
import TaskDetailsModal from './TaskDetailsModal';
import api from '../api';
import Swal from 'sweetalert2';

export default function TaskManager() {
  const [viewMode, setViewMode] = useState('Grouped');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [selectedEmployeeForTasks, setSelectedEmployeeForTasks] = useState(null);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [statsRes, empRes, tasksRes] = await Promise.all([
        api.get('/admin/tasks/stats'),
        api.get('/admin/tasks/employee-performance'),
        api.get('/admin/tasks?all=true')
      ]);
      setStats(statsRes.data);
      setEmployees(empRes.data);
      setTasks(tasksRes.data.data || tasksRes.data);
    } catch (err) {
      console.error("Error fetching tasks data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setTaskToEdit(task);
    setIsCreateModalOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const { isConfirmed } = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });

      if (isConfirmed) {
        await api.delete(`/admin/tasks/${taskId}`);
        Swal.fire('Deleted!', 'Task has been deleted.', 'success');
        fetchAllData();
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      Swal.fire('Error', 'Failed to delete task', 'error');
    }
  };

  const STATS_CARDS = [
    { label: 'Total', count: stats.total || 0, icon: ClipboardList, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', value: 'all' },
    { label: 'Pending', count: stats.pending || 0, icon: Clock, color: 'text-amber-500', bg: 'bg-[#fffdf0]', border: 'border-[#fde68a]', value: 'pending' },
    { label: 'In Progress', count: stats.in_progress || 0, icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', value: 'in_progress' },
    { label: 'Completed', count: stats.completed || 0, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', value: 'completed' },
    { label: 'Not Done', count: stats.not_done || 0, icon: XCircle, color: 'text-red-500', bg: 'bg-white', border: 'border-red-200', value: 'not_done' },
    { label: 'Overdue', count: stats.overdue || 0, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', value: 'overdue' },
    { label: 'Today', count: stats.today || 0, icon: CalendarIcon, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', value: 'today' },
  ];

  const filteredTasks = tasks.filter(task => {
    // Search filter
    const searchMatch = !searchQuery || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      task.assignee?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Status filter
    let statusMatch = true;
    if (statusFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        statusMatch = task.due_date?.startsWith(today);
    } else if (statusFilter !== 'all') {
        statusMatch = task.status === statusFilter;
    }

    // Priority filter
    const priorityMatch = priorityFilter === 'all' || task.priority === priorityFilter;

    // Employee filter
    const employeeMatch = employeeFilter === 'all' || task.assigned_to?.toString() === employeeFilter;

    // Date range filter
    let dateMatch = true;
    if (dateFrom) {
        dateMatch = dateMatch && task.due_date >= dateFrom;
    }
    if (dateTo) {
        dateMatch = dateMatch && task.due_date <= dateTo;
    }

    return searchMatch && statusMatch && priorityMatch && employeeMatch && dateMatch;
  });

  const filteredEmployees = employees.map(emp => {
    const empTasks = filteredTasks.filter(t => t.assigned_to === emp.id);
    return {
      ...emp,
      filtered_total: empTasks.length,
      filtered_overdue: empTasks.filter(t => t.status === 'overdue').length,
      filtered_pending: empTasks.filter(t => t.status === 'pending').length,
      filtered_completed: empTasks.filter(t => t.status === 'completed').length,
    };
  }).filter(emp => emp.filtered_total > 0 || (searchQuery === '' && statusFilter === 'all' && priorityFilter === 'all' && employeeFilter === 'all'));

  const formatStatus = (status) => {
    switch(status) {
      case 'overdue': return 'Overdue';
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'not_done': return 'Not Done';
      default: return 'Pending';
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'overdue': return { bg: 'bg-red-50', text: 'text-red-500', border: 'border-red-100', icon: AlertCircle };
      case 'completed': return { bg: 'bg-emerald-50', text: 'text-emerald-500', border: 'border-emerald-100', icon: CheckCircle };
      case 'in_progress': return { bg: 'bg-blue-50', text: 'text-blue-500', border: 'border-blue-100', icon: RefreshCw };
      case 'not_done': return { bg: 'bg-gray-50', text: 'text-red-500', border: 'border-red-200', icon: XCircle };
      default: return { bg: 'bg-amber-50', text: 'text-amber-500', border: 'border-amber-100', icon: Clock };
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Top Header Row */}
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center border border-purple-200 shadow-sm shrink-0">
            <ClipboardList size={22} strokeWidth={2.5} />
          </div>
          <h1 className="text-xl font-black text-slate-800 tracking-tight whitespace-nowrap">Task Manager</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Moved Date Filters */}
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">From</span>
            <input 
              type="date" 
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-[12px] font-bold text-slate-600 focus:outline-none bg-transparent" 
            />
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">To</span>
            <input 
              type="date" 
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-[12px] font-bold text-slate-600 focus:outline-none bg-transparent" 
            />
          </div>

          <button 
            onClick={() => setIsPerformanceModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 font-bold text-[13px] rounded-lg hover:bg-slate-50 transition-colors shadow-sm bg-white h-10"
          >
            <BarChart2 size={16} /> Performance
          </button>
          <button 
            onClick={() => setIsBulkModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#f97316] text-white font-bold text-[13px] rounded-lg hover:bg-[#ea580c] transition-colors shadow-sm h-10"
          >
            <Users size={16} /> Bulk Assign
          </button>
          <button 
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#a855f7] text-white font-bold text-[13px] rounded-lg hover:bg-[#9333ea] transition-colors shadow-sm h-10"
          >
            <Plus size={16} strokeWidth={3} /> New Task
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {STATS_CARDS.map((stat, i) => (
          <div 
            key={i} 
            onClick={() => setStatusFilter(stat.value)}
            className={`rounded-xl px-3 py-2.5 border cursor-pointer transition-all active:scale-95 ${stat.value === statusFilter ? 'ring-2 ring-purple-500 ring-offset-2' : ''} ${stat.bg} ${stat.border} shadow-sm`}
          >
            <div className="flex items-center justify-between mb-1">
              <stat.icon size={15} className={stat.color} strokeWidth={2.5} />
              <span className={`text-2xl font-black leading-none ${stat.color}`}>{stat.count}</span>
            </div>
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-tighter">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Actions & Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 space-y-4">
        
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by task title or name..." 
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors shadow-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded-lg text-[11px] font-black uppercase tracking-wider text-slate-600 py-2 px-3 focus:outline-none shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px] min-w-[120px]"
            >
              <option value="all">ALL STATUS</option>
              <option value="pending">PENDING</option>
              <option value="in_progress">IN PROGRESS</option>
              <option value="completed">COMPLETED</option>
              <option value="not_done">NOT DONE</option>
              <option value="overdue">OVERDUE</option>
              <option value="today">DUE TODAY</option>
            </select>
            
            <select 
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="border border-slate-200 rounded-lg text-[11px] font-black uppercase tracking-wider text-slate-600 py-2 px-3 focus:outline-none shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px] min-w-[120px]"
            >
              <option value="all">ALL PRIORITY</option>
              <option value="low">LOW</option>
              <option value="medium">MEDIUM</option>
              <option value="high">HIGH</option>
              <option value="urgent">URGENT</option>
            </select>

            <select 
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="border border-slate-200 rounded-lg text-[11px] font-black uppercase tracking-wider text-slate-600 py-2 px-3 focus:outline-none shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%2364748B%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[center_right_10px] min-w-[150px]"
            >
              <option value="all">ALL EMPLOYEES</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id.toString()}>{emp.name.toUpperCase()}</option>
              ))}
            </select>

            <div className="flex items-center p-0.5 border border-slate-200 rounded-lg bg-slate-50/50 shadow-sm ml-2">
              <button 
                onClick={() => setViewMode('Grouped')}
                className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-wider rounded-md transition-colors flex items-center gap-1 ${viewMode === 'Grouped' ? 'bg-white text-purple-600 shadow-sm border border-slate-200/50' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Layers size={14} /> Grouped
              </button>
              <button 
                onClick={() => setViewMode('List')}
                className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-wider rounded-md transition-colors flex items-center gap-1 ${viewMode === 'List' ? 'bg-white text-purple-600 shadow-sm border border-slate-200/50' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <List size={14} /> List
              </button>
            </div>

            <button onClick={fetchAllData} className="p-2 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shadow-sm ml-1">
              <RotateCcw size={16} className={loading && "animate-spin"} />
            </button>
          </div>
        </div>
      </div>
      {loading && <div className="py-20 text-center font-bold text-slate-400">Loading Task Data...</div>}

      {/* Main Table Content */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden min-h-[500px]">
          {viewMode === 'Grouped' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="px-6 py-4">EMPLOYEE</th>
                    <th className="px-4 py-4 w-1/3">TASK SUMMARY</th>
                    <th className="px-4 py-4 text-center">TOTAL TASKS</th>
                    <th className="px-6 py-4 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 border-t border-slate-100">
                  {filteredEmployees.length > 0 ? filteredEmployees.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-2.5">
                        <div className="flex items-center gap-3">
                          {row.profile_photo_url ? (
                            <img src={row.profile_photo_url} alt={row.name} className="w-9 h-9 rounded-full object-cover shadow-sm border border-slate-100" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px] shadow-sm uppercase tracking-tight">
                              {getInitials(row.name)}
                            </div>
                          )}
                          <div>
                            <div className="text-[13px] font-black text-slate-800">{row.name}</div>
                            <div className="text-[11px] font-semibold text-slate-400 mt-0.5">{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          {row.filtered_overdue > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold shadow-sm border bg-red-50 text-red-500 border-red-100">
                              <AlertCircle size={12} strokeWidth={2.5} /> {row.filtered_overdue} Overdue
                            </span>
                          )}
                          {row.filtered_pending > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold shadow-sm border bg-amber-50 text-amber-500 border-amber-100">
                              <Clock size={12} strokeWidth={2.5} /> {row.filtered_pending} Pending
                            </span>
                          )}
                          {row.filtered_completed > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold shadow-sm border bg-emerald-50 text-emerald-500 border-emerald-100">
                              <CheckCircle size={12} strokeWidth={2.5} /> {row.filtered_completed} Done
                            </span>
                          )}
                          {row.filtered_total === 0 && (
                            <span className="text-slate-400 text-xs font-semibold">No tasks match filters</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <span className="text-[14px] font-black text-slate-700">{row.filtered_total}</span>
                      </td>
                      <td className="px-6 py-2.5 text-right">
                        <button 
                          onClick={() => setSelectedEmployeeForTasks(row)}
                          className="inline-flex items-center gap-1.5 px-4 py-2 border border-purple-100 rounded-lg bg-purple-50 hover:bg-purple-100 text-[11px] font-bold text-purple-600 transition-colors shadow-sm tracking-wide"
                        >
                          <Eye size={14} strokeWidth={2.5} /> View Tasks
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="text-center py-10 font-bold text-slate-400">No employees found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                    <th className="px-6 py-4 w-1/4">TASK</th>
                    <th className="px-4 py-4">ASSIGNEE</th>
                    <th className="px-4 py-4">PRIORITY</th>
                    <th className="px-4 py-4">STATUS</th>
                    <th className="px-4 py-4">DUE DATE</th>
                    <th className="px-6 py-4 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 border-t border-slate-100">
                  {filteredTasks.length > 0 ? filteredTasks.map((row) => {
                    const statusConfig = getStatusColor(row.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                    <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-2.5">
                        <div className="text-[13px] font-black text-slate-800">{row.title}</div>
                        {row.category && (
                          <div className="text-[11px] font-bold text-blue-500 bg-blue-50 inline-block px-1.5 mt-1 rounded border border-blue-100 bg-opacity-50">{row.category}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-[8px] shadow-sm uppercase bg-purple-100 text-purple-600 border border-purple-200 tracking-tighter">
                            {getInitials(row.assignee?.name || 'U')}
                          </div>
                          <span className="text-[12px] font-bold text-slate-600">{row.assignee?.name || 'Unassigned'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] font-black uppercase ${row.priority === 'urgent' ? 'text-red-500 font-extrabold' : row.priority === 'high' ? 'text-orange-500' : 'text-slate-500'}`}>{row.priority}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className={`inline-flex items-center justify-between w-28 px-3 py-1.5 border rounded-lg ${statusConfig.bg} ${statusConfig.border}`}>
                          <span className={`text-[11px] font-bold ${statusConfig.text}`}>{formatStatus(row.status)}</span>
                          <span className={`${statusConfig.text}`}><StatusIcon size={14} /></span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-1.5 text-[12px] font-bold text-slate-500">
                          <CalendarIcon size={12} /> {new Date(row.due_date).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2 text-slate-400">
                          <button onClick={() => setSelectedTaskForDetails(row)} className="hover:text-purple-500 transition-colors p-1"><Eye size={16} /></button>
                          <button onClick={() => handleEditTask(row)} className="hover:text-emerald-500 transition-colors p-1"><Edit2 size={16} /></button>
                          <button onClick={() => handleDeleteTask(row.id)} className="hover:text-red-500 transition-colors p-1"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  )}) : (
                    <tr>
                      <td colSpan="6" className="text-center py-10 font-bold text-slate-400 uppercase tracking-[0.2em] text-[10px]">No matching tasks found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Re-fetch data seamlessly when modals are closed because new tasks might be assigned */}
      <CreateTaskModal 
        isOpen={isCreateModalOpen} 
        onClose={() => { 
          setIsCreateModalOpen(false); 
          setTaskToEdit(null);
          fetchAllData(); 
        }} 
        task={taskToEdit}
        employees={employees}
      />
      <BulkAssignTaskModal isOpen={isBulkModalOpen} onClose={() => { setIsBulkModalOpen(false); fetchAllData(); }} employees={employees} />
      <PerformanceReportModal isOpen={isPerformanceModalOpen} onClose={() => setIsPerformanceModalOpen(false)} />
      
      {/* New Detail Modals */}
      <EmployeeTasksModal 
        isOpen={!!selectedEmployeeForTasks} 
        onClose={() => setSelectedEmployeeForTasks(null)} 
        employee={selectedEmployeeForTasks} 
        tasks={tasks}
        onViewTask={(task) => setSelectedTaskForDetails(task)}
        onUpdate={fetchAllData}
        onEditTask={handleEditTask}
        onDeleteTask={handleDeleteTask}
      />

      <TaskDetailsModal 
        isOpen={!!selectedTaskForDetails} 
        onClose={() => setSelectedTaskForDetails(null)} 
        task={selectedTaskForDetails} 
      />
    </div>
  );
}
